import * as Sentry from '@sentry/react'
import type { Node, Edge } from '@xyflow/react'
import type { MindMapNode, MindMapEdge } from '@bandos/shared'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { createNodeData, createEdge } from '../../../utils'

// ============================================================================
// TYPES
// ============================================================================

export interface TransformConfig {
  mindmapId?: string
}

export interface TransformResult {
  nodes: Node<ReactFlowNodeData>[]
  edges: Edge[]
}

// ============================================================================
// NODE TRANSFORMATION
// ============================================================================

/**
 * Transforms a single AI-generated node to React Flow format.
 *
 * @param aiNode - The backend node format from AI service
 * @param mindmapId - The mindmap ID for node data creation
 * @returns A React Flow node
 */
function transformAINodeToReactFlow(
  aiNode: MindMapNode,
  mindmapId?: string
): Node<ReactFlowNodeData> {
  const nodeData = createNodeData({
    nodeId: aiNode.id,
    category: aiNode.data.category,
    mindmapId,
    initialData: {
      ...aiNode.data,
      id: aiNode.id,
    },
  })

  return {
    id: aiNode.id,
    position: aiNode.position,
    data: nodeData,
    type: `${aiNode.data.category}Node`,
  }
}

/**
 * Transforms multiple AI-generated nodes to React Flow format.
 *
 * @param aiNodes - Array of backend nodes from AI service
 * @param mindmapId - The mindmap ID for node data creation
 * @returns Array of React Flow nodes
 */
function transformAINodesToReactFlow(
  aiNodes: MindMapNode[],
  mindmapId?: string
): Node<ReactFlowNodeData>[] {
  return aiNodes.map((aiNode) => transformAINodeToReactFlow(aiNode, mindmapId))
}

// ============================================================================
// EDGE TRANSFORMATION
// ============================================================================

/**
 * Transforms AI-generated edges to React Flow format.
 *
 * @param aiEdges - Array of backend edges from AI service
 * @param transformedNodes - The already-transformed React Flow nodes
 * @returns Array of React Flow edges
 */
function transformAIEdgesToReactFlow(
  aiEdges: MindMapEdge[],
  transformedNodes: Node<ReactFlowNodeData>[]
): Edge[] {
  // Convert AI edges to minimal edge format for createEdge utility
  const minimalEdges = aiEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: 'default' as const,
  }))

  return aiEdges.map((edge) =>
    createEdge(edge.source, edge.target, transformedNodes, minimalEdges, {
      animated: edge.animated,
      sourceHandle: 'right-source',
      targetHandle: 'target',
    })
  )
}

// ============================================================================
// COMBINED TRANSFORMATION
// ============================================================================

/**
 * Transforms both AI nodes and edges to React Flow format in one call.
 *
 * @param aiNodes - Array of backend nodes from AI service
 * @param aiEdges - Array of backend edges from AI service
 * @param config - Configuration options (mindmapId)
 * @returns Object containing transformed nodes and edges
 */
export function transformAIDataToReactFlow(
  aiNodes: MindMapNode[],
  aiEdges: MindMapEdge[],
  config: TransformConfig = {}
): TransformResult {
  const nodes = transformAINodesToReactFlow(aiNodes, config.mindmapId)
  const edges = transformAIEdgesToReactFlow(aiEdges, nodes)

  return { nodes, edges }
}

// ============================================================================
// NODE MERGING
// ============================================================================

/**
 * Merges transformed AI nodes with existing nodes.
 * Updates existing nodes if IDs match, adds new nodes otherwise.
 *
 * @param existingNodes - Current nodes in the mindmap
 * @param newNodes - Transformed AI nodes to merge
 * @returns Merged node array
 */
export function mergeAINodesWithExisting(
  existingNodes: Node<ReactFlowNodeData>[],
  newNodes: Node<ReactFlowNodeData>[]
): Node<ReactFlowNodeData>[] {
  const existingNodeIds = new Set(existingNodes.map((n) => n.id))
  const nodesToAdd: Node<ReactFlowNodeData>[] = []
  const nodesToUpdate: Node<ReactFlowNodeData>[] = []

  // Separate new nodes from updated nodes
  for (const node of newNodes) {
    if (existingNodeIds.has(node.id)) {
      nodesToUpdate.push(node)
    } else {
      nodesToAdd.push(node)
    }
  }

  // Merge: update existing nodes, add new nodes
  const mergedNodes = existingNodes.map((existingNode) => {
    const updated = nodesToUpdate.find((u) => u.id === existingNode.id)
    return updated ?? existingNode
  })

  return [...mergedNodes, ...nodesToAdd]
}

/**
 * Merges transformed AI edges with existing edges.
 * Prevents duplicate edges by checking both edge IDs and source-target pairs.
 *
 * @param existingEdges - Current edges in the mindmap
 * @param newEdges - Transformed AI edges to merge
 * @returns Merged edge array without duplicates
 */
export function mergeAIEdgesWithExisting(existingEdges: Edge[], newEdges: Edge[]): Edge[] {
  const existingEdgeIds = new Set(existingEdges.map((e) => e.id))

  // Track existing source-target pairs to prevent duplicate connections
  const existingConnections = new Set(existingEdges.map((e) => `${e.source}->${e.target}`))

  const conflictingIds = newEdges.filter((e) => existingEdgeIds.has(e.id)).map((e) => e.id)
  if (conflictingIds.length > 0) {
    console.error(`❌ [AI] CONFLICTING EDGE IDS WITH EXISTING EDGES:`, conflictingIds)
    Sentry.captureException(
      new Error(`[AI] Conflicting edge IDs with existing edges: ${conflictingIds.join(', ')}`),
      { tags: { feature: 'mindmap', action: 'merge_ai_edges' } }
    )
  }

  // Filter out edges that would create duplicate connections
  const uniqueNewEdges = newEdges.filter((edge) => {
    const connectionKey = `${edge.source}->${edge.target}`
    if (existingConnections.has(connectionKey)) {
      console.warn(`⚠️ [AI] Skipping duplicate edge: ${edge.source} -> ${edge.target}`)
      return false
    }
    // Add to set to also prevent duplicates within newEdges
    existingConnections.add(connectionKey)
    return true
  })

  return [...existingEdges, ...uniqueNewEdges]
}
